<template>
  <div class="table-list-container">
    <div v-if="loading">
      <Spin
        :pointNum="8"
        :translateX="25"
        background="var(--base-primary-bgColor)" />
    </div>
    <div
      v-else
      class="table-list-box">
      <div class="table-list-content-box">
        <div class="common-table-box">
          <el-table
            class="publish-table"
            highlight-selection-row
            :data="sceneList"
            @select="selectChange"
            @select-all="selectChange"
            :row-class-name="tableRowClassName"
            :header-row-class-name="customHeader"
            height="100%"
            ref="modelTable">
            <el-table-column
              type="selection"
              width="60" />
            <el-table-column
              label="序号"
              type="index"
              width="55" />
            <el-table-column
              label="场景名称"
              width="340">
              <template v-slot="{ row }">
                <div
                  class="scene-name-box"
                  v-permission="'Project.Single.Scene.View'">
                  <div class="icon-box">
                    <icon-svg
                      size="18"
                      v-if="row.displayMode === 'Sphere'"
                      icon-class="ic-sphere"
                      title="三维球体" />
                    <icon-svg
                      size="18"
                      v-if="row.displayMode === 'Plane'"
                      icon-class="ic-plane"
                      title="平面" />
                  </div>
                  <div
                    class="text-overflow scene-name"
                    :title="row.sceneName"
                    @click="viewScene(row)">
                    {{ row.sceneName }}
                  </div>
                  <div
                    class="default-view-box"
                    v-if="row.defaultViewStatus === 1">
                    默认场景
                  </div>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="创建日期">
              <template v-slot="{ row }">
                <span>{{ formatTime(row.creationTime) }}</span>
              </template>
            </el-table-column>
            <el-table-column label="更新日期">
              <template v-slot="{ row }">
                <span>{{
                  row.lastModificationTime
                    ? formatTime(row.lastModificationTime)
                    : formatTime(row.creationTime)
                }}</span>
              </template>
            </el-table-column>
            <el-table-column label="状态">
              <template v-slot="{ row }">
                <div
                  class="status-box"
                  v-if="row.status === 0">
                  <div class="circle bg-color1"></div>
                  <span class="color1">未发布</span>
                  <el-tooltip
                    :effect="$theme.tooltipKey()"
                    v-if="row.latestConvertStatus === 4"
                    placement="top-start"
                    popper-class="common-panel-tooltip-box">
                    <div slot="content">该场景最近一次未发布成功</div>
                    <div class="icon-box">
                      <icon-svg
                        icon-class="ic-wenhao1"
                        class="color3" />
                    </div>
                  </el-tooltip>
                </div>
                <div
                  class="status-box"
                  v-if="row.status === 2 || row.status === 3">
                  <div class="circle bg-color2"></div>
                  <span class="color2">发布中</span>
                </div>
                <div
                  class="status-box"
                  v-if="row.status === 1">
                  <div class="circle bg-color4"></div>
                  <span class="color4">已发布</span>
                  <el-tooltip
                    :effect="$theme.tooltipKey()"
                    v-if="row.latestConvertStatus === 4"
                    placement="top-start"
                    popper-class="common-panel-tooltip-box">
                    <div slot="content">该场景最近一次未发布成功</div>
                    <div class="icon-box">
                      <icon-svg
                        icon-class="ic-wenhao1"
                        class="color3" />
                    </div>
                  </el-tooltip>
                  <el-tooltip
                    :effect="$theme.tooltipKey()"
                    v-if="
                      row.latestConvertStatus === 1 &&
                      (row.hasDataSetUpdate || row.hasClUpdate)
                    "
                    placement="top-start"
                    popper-class="common-panel-tooltip-box">
                    <div slot="content">该场景发布后有更新</div>
                    <div class="icon-box">
                      <icon-svg
                        icon-class="ic-jiancegengxin"
                        class="color4" />
                    </div>
                  </el-tooltip>
                </div>
              </template>
            </el-table-column>
            <el-table-column
              label="坐标系"
              width="200">
              <template v-slot="{ row }">
                <span
                  class="text-overflow"
                  :title="`${row.coordinates ? row.coordinates : '--'}`">
                  {{ row.coordinates ? row.coordinates : '--' }}
                </span>
              </template>
            </el-table-column>
            <el-table-column
              label="操作"
              v-if="isOperationColumnVisible()"
              :width="getOperationColumnWidth()">
              <template v-slot="{ row }">
                <div class="table-actions operation-cell">
                  <el-button
                    v-if="isChecked('Project.Single.Scene.Edit')"
                    size="small"
                    type="text"
                    :disabled="row.status === 2 || row.status === 3"
                    @click="editScene(row)">
                    编辑场景
                  </el-button>
                  <el-tooltip
                    :effect="$theme.tooltipKey()"
                    :disabled="row.status === 1"
                    placement="top-start"
                    offset="10"
                    content="请先编辑并发布场景">
                    <el-button
                      v-if="isChecked('Project.Single.Scene.Share')"
                      size="small"
                      type="text"
                      :disabled="row.status !== 1"
                      @click="shareScene(row)">
                      分享
                    </el-button>
                  </el-tooltip>
                  <el-button
                    v-if="isChecked('Project.Single.Scene.Delete')"
                    size="small"
                    type="text"
                    :disabled="row.status === 2 || row.status === 3"
                    @click="deleteSceneBySceneId(row)">
                    删除
                  </el-button>
                  <div
                    class="common-dropdown-box"
                    v-if="isMoreButtonsVisible()">
                    <el-dropdown
                      placement="bottom-start"
                      @command="command => moreOperate(command, row)">
                      <el-button
                        type="text"
                        size="small"
                        key="moreButtons">
                        更多
                      </el-button>
                      <el-dropdown-menu
                        slot="dropdown"
                        class="common-dropdown-menu dropdown-primary dropdown--table-primary custom-dropdown-primary-sceneList">
                        <!-- <el-dropdown-item
                          command="1"
                          v-if="isChecked('Project.Single.Scene.Copy')"
                          >复制</el-dropdown-item
                        > -->
                        <div
                          v-if="isChecked('Project.Single.Scene.SetDefault')">
                          <el-tooltip
                            :effect="$theme.tooltipKey()"
                            v-if="isSetDefaultSceneDisabled(row)"
                            placement="left"
                            :offset="-3">
                            <div slot="content">
                              <span v-if="row.status !== 1">
                                请先编辑并发布场景
                              </span>
                              <span v-if="row.defaultViewStatus === 1">
                                当前场景已经是默认场景
                              </span>
                            </div>
                            <div :class="getDropdownItemClassName(row)">
                              <el-dropdown-item
                                command="2"
                                :disabled="isSetDefaultSceneDisabled(row)">
                                设为默认场景
                              </el-dropdown-item>
                            </div>
                          </el-tooltip>
                        </div>
                        <el-dropdown-item
                          command="3"
                          v-if="isChecked('Project.Single.Scene.SetThumbnail')">
                          编辑基本信息
                        </el-dropdown-item>
                      </el-dropdown-menu>
                    </el-dropdown>
                  </div>
                </div>
              </template>
            </el-table-column>
            <template slot="empty">
              <div class="common-empty-box">
                <bj-empty
                  :image="getNoDataImg()"
                  class="empty-page">
                  <template slot="description">
                    <div
                      :class="`${
                        isMicroAppMode()
                          ? 'description-box empty-text'
                          : 'empty-text'
                      }`">
                      抱歉，没有找到相关内容
                    </div>
                  </template>
                </bj-empty>
              </div>
            </template>
          </el-table>
        </div>
        <div class="common-pagination-box pagination-primary">
          <el-pagination
            popper-class="common-pagination-popper-box"
            layout="sizes, prev, pager, next, jumper"
            :page-sizes="[10, 20, 50, 100, 150, 200]"
            :page-size="pageSize"
            :current-page="currentPage"
            :total="total"
            @size-change="sizeChange"
            @current-change="pageChange">
          </el-pagination>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Spin from '@/components/Spin.vue'
import { Store } from '@/utils/store'
import { isMicroAppMode, helper } from '@/microAdapt/utils'
import { formatTime } from '@/utils/generalMethods'

import {
  getSceneListService,
  deleteSceneService,
  batchDeleteSceneService,
  setDefaultViewService
} from '@/service/interface'
export default {
  data() {
    return {
      loading: false,
      currentPage: 1,
      pageSize: 10,
      total: 0,
      sceneList: [],
      selectedScenes: [],
      customHeader: ''
    }
  },
  props: {
    keywords: String,
    updateTotal: Function,
    openShareDialog: Function,
    openSceneNameDialog: Function,
    openSceneInfoDialog: Function
  },
  components: {
    Spin
  },
  mounted() {
    this.initSceneList()
    setTimeout(() => {
      console.log('renu')

      const currentBody = this.$refs.modelTable?.bodyWrapper
      currentBody &&
        currentBody.addEventListener('scroll', ev => {
          const scrollTop = ev.target.scrollTop
          this.customHeader = scrollTop > 10 ? 'darkHeader' : ''
        })
    }, 1500)
  },
  beforeDestroy() {
    this.timer && clearInterval(this.timer)
    this.timer = null
  },
  methods: {
    initSceneList() {
      this.loading = true
      this.currentPage = 1
      this.querySceneList().then(response => {
        let { sceneList, total } = response
        this.sceneList = sceneList
        this.total = total
        this.updateTotal(total)

        this.selectedScenes = []

        this.loading = false

        let timeFlag = this.sceneList.some(el => el.isPublishing)
        if (timeFlag && !this.timer) {
          this.timer = setInterval(this.refreshSceneList, 1000)
        }
      })
    },
    viewScene(sceneInfo) {
      if (!this.isChecked('Project.Single.Scene.View')) {
        this.$message.error('当前用户没有查看场景的权限')
        return
      }

      let status = sceneInfo.status
      if (status === 0) {
        this.$message.warning('场景未发布，请发布后查看')
        return
      }

      if (status === 2 || status === 3) {
        this.$message.warning('场景正在发布中，请发布后查看')
        return
      }

      if (!sceneInfo.hasComponents && !sceneInfo.hasDataSets) {
        this.$message.warning('当前场景无有效数据')
        return
      }
      if (typeof helper.previewScene === 'function') {
        helper.previewScene({ sceneId: sceneInfo.sceneId })
      } else {
        let projId = this.$route.params.projId
        let projName = this.$route.params.projName
        let { dataType, theme, isHeaderHidden } = this.$route.query
        let sceneId = sceneInfo.sceneId
        let viewMode = sceneInfo.displayMode
        projName = encodeURIComponent(projName)

        let url = `/singleProject/sceneView/${projId}/${projName}?sceneId=${sceneId}&viewMode=${viewMode}&dataType=${dataType}`
        if (theme) {
          url += `&theme=${theme}`
        }
        if (isHeaderHidden) {
          url += `&isHeaderHidden=false`
        }
        this.$router.push(url)
      }
    },
    editScene(sceneInfo) {
      let projId = this.$route.params.projId
      let projName = this.$route.params.projName
      let { dataType, theme, isHeaderHidden } = this.$route.query
      let sceneId = sceneInfo.sceneId
      let viewMode = sceneInfo.displayMode
      projName = encodeURIComponent(projName)

      let url = `/singleProject/sceneEdit/${projId}/${projName}?sceneId=${sceneId}&viewMode=${viewMode}&dataType=${dataType}`
      if (theme) {
        url += `&theme=${theme}`
      }
      if (isHeaderHidden) {
        url += `&isHeaderHidden=false`
      }
      this.$router.push(url)
    },
    copyScene(sceneInfo) {
      this.openSceneNameDialog(sceneInfo)
    },
    shareScene(sceneInfo) {
      this.openShareDialog([sceneInfo])
    },
    deleteSceneBySceneId(sceneInfo) {
      this.$confirm('确定删除该场景?', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      })
        .then(() => {
          let sceneId = sceneInfo.sceneId
          deleteSceneService({ sceneId }).then(response => {
            if (response.isSuccess) {
              this.initSceneList()
              this.$message.success('删除成功！！！')
            } else {
              this.$message.error(response.errMsg)
            }
          })
        })
        .catch(() => {
          // this.$message.info('取消删除')
        })
    },
    moreOperate(command, sceneInfo) {
      switch (command) {
        case '1':
          this.copyScene(sceneInfo)
          break
        case '2':
          this.setDefaultView(sceneInfo)
          break
        case '3':
          this.openSceneInfoDialog(sceneInfo)
          break
      }
    },
    setDefaultView(sceneInfo) {
      let defaultViewStatus = sceneInfo.defaultViewStatus
      if (defaultViewStatus === 1) {
        this.$message.error('当前场景已经是默认场景！！！')
        return
      }
      let params = {
        sceneId: sceneInfo.sceneId
      }
      setDefaultViewService(params).then(response => {
        if (response.isSuccess) {
          this.refreshSceneList()
          this.$message.success('设置默认场景成功！！！')
        } else {
          this.$message.error(response.errMsg)
        }
      })
    },
    refreshSceneList() {
      this.querySceneList().then(response => {
        let { sceneList, total } = response
        let flag = sceneList.some(el => el.isPublishing)
        if (!flag) {
          this.timer && clearInterval(this.timer)
          this.timer = null
        }

        this.sceneList = sceneList
        this.total = total
        this.updateTotal(total)
      })
    },
    batchShare() {
      let scenes = this.selectedScenes
      if (!scenes.length) {
        this.$message.warning('请先选择需要分享的场景！')
        return
      }

      let invalidScenes = this.selectedScenes.filter(el => el.status !== 1)
      if (invalidScenes.length) {
        this.$message.warning('仅已发布的场景支持分享，请重新选择！')
        return
      }

      this.openShareDialog(scenes)
    },
    batchDelete() {
      let scenes = this.selectedScenes
      if (!scenes.length) {
        this.$message.warning('请先选择需要删除的场景！')
        return
      }

      let invalidScenes = this.selectedScenes.filter(el => el.isPublishing)
      if (invalidScenes.length) {
        this.$message.warning('发布中的场景不支持删除，请重新选择！')
        return
      }

      this.$confirm('确定删除这些场景?', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      })
        .then(() => {
          let sceneIds = scenes.map(el => el.sceneId)
          batchDeleteSceneService({ sceneIds }).then(response => {
            if (response.isSuccess) {
              this.$message.success('删除成功!')
              this.initSceneList()
            } else {
              this.$message.error(response.errMsg)
            }
          })
        })
        .catch(() => {
          // this.$message.info('取消删除!')
        })
    },
    selectChange(selection) {
      this.selectedScenes = selection
    },
    querySceneList() {
      return new Promise(resolve => {
        let projId = this.$route.params.projId
        let params = {
          projId,
          resultCount: this.pageSize,
          skipCount: (this.currentPage - 1) * this.pageSize,
          sceneName: this.keywords
        }
        getSceneListService(params).then(response => {
          if (response.isSuccess) {
            let data = response.data
            let sceneList = data.items
            let total = data.totalCount
            resolve({ sceneList, total })
          } else {
            this.$message.error(response.errMsg)
          }
        })
      })
    },
    sizeChange(pageSize) {
      this.pageSize = pageSize
      this.querySceneList().then(response => {
        let { sceneList, total } = response
        this.sceneList = sceneList
        this.total = total
        this.updateTotal(total)

        let timeFlag = this.sceneList.some(el => el.isPublishing)
        if (timeFlag && !this.timer) {
          this.timer = setInterval(this.refreshSceneList, 1000)
        }
      })
    },
    pageChange(currentPage) {
      this.currentPage = currentPage
      this.querySceneList().then(response => {
        let { sceneList, total } = response
        this.sceneList = sceneList
        this.total = total
        this.updateTotal(total)

        let timeFlag = this.sceneList.some(el => el.isPublishing)
        if (timeFlag && !this.timer) {
          this.timer = setInterval(this.refreshSceneList, 1000)
        }
      })
    },
    tableRowClassName(row) {
      if (row.isPublishing) {
        return 'row-disabled'
      } else {
        return ''
      }
    },
    getDropdownItemClassName(sceneInfo) {
      let className = 'dropdown-item-box'
      if (this.isSetDefaultSceneDisabled(sceneInfo)) {
        className += ' is-disabled'
      }
      return className
    },
    isSetDefaultSceneDisabled(sceneInfo) {
      return sceneInfo.status !== 1 || sceneInfo.defaultViewStatus === 1
    },
    isOperationColumnVisible() {
      const permissionCodeArray = [
        'Project.Single.Scene.Edit',
        'Project.Single.Scene.Share',
        'Project.Single.Scene.Delete'
      ]

      // prettier-ignore
      return permissionCodeArray.some(permissionCode => this.isChecked(permissionCode)) || this.isMoreButtonsVisible()
    },
    isMoreButtonsVisible() {
      const moreBtnPerssionCodeArray = [
        'Project.Single.Scene.SetDefault',
        'Project.Single.Scene.SetThumbnail'
      ]

      return moreBtnPerssionCodeArray.some(code => this.isChecked(code))
    },
    getOperationColumnWidth() {
      const permissionCodeArray = [
        'Project.Single.Scene.Edit',
        'Project.Single.Scene.Share',
        'Project.Single.Scene.Delete'
      ]

      // prettier-ignore
      let btnCount = permissionCodeArray.filter(code => this.isChecked(code)).length
      if (this.isMoreButtonsVisible()) {
        btnCount += 1
      }

      return btnCount * 60
    },
    /**
     * 权限验证
     */
    isChecked(permissionCode) {
      let permissionData = Store.permissionData
      let find = permissionData.find(el => el.permissionCode === permissionCode)
      return find.isCheck
    },
    getNoDataImg() {
      if (isMicroAppMode()) {
        return require('@/assets/img/no-data-nebula.png')
      } else {
        return require('@/assets/img/no-data.png')
      }
    },
    formatTime,
    isMicroAppMode
  }
}
</script>

<style lang="less" scoped>
.table-list-container {
  height: calc(100% - 148px);
  margin-top: 10px;

  .table-list-box {
    height: 100%;

    .table-list-content-box {
      height: 100%;

      .common-table-box {
        position: relative;
        height: 100%;

        /deep/ .el-table {
          position: absolute;

          .scene-name-box {
            display: flex;
            align-items: center;
            color: var(--table-text-color);

            .icon-box {
              margin-right: 5px;
              margin-top: 4px;
            }

            .default-view-box {
              margin-left: 8px;
              margin-top: 1px;
              height: 20px;
              display: flex;
              align-items: center;
              padding: 0 4px;
              border-radius: 4px;
              color: var(--base-primary-bgColor);
              font-size: 12px;
              border: 1px solid var(--base-primary-bgColor);
              background: var(--base-bg-light-color24);
            }
          }

          .row-disabled {
            color: var(--base-dark9-color);
          }

          .description-box {
            margin-top: 50px;
          }

          .table-actions {
            display: flex;
            justify-content: space-between;

            .el-button {
              margin-right: 0px;
            }

            .common-dropdown-box {
              .el-dropdown {
                margin-right: 6px;
              }

              .el-dropdown-menu {
                width: 120px;
              }

              .dropdown-item-box {
                border-bottom: 1px solid var(--dropdowm-border-color);

                &:last-of-type {
                  border-bottom: none;
                }

                &.is-disabled {
                  cursor: not-allowed;
                }
              }
            }
          }

          .scene-name {
            cursor: pointer;

            &:hover {
              color: var(--base-primary-bgColor);
              text-decoration: underline;
            }
          }

          .status-box {
            display: flex;
            align-items: center;
            font-size: 12px;

            .circle {
              width: 5px;
              height: 5px;
              margin: auto 5px auto 0px;
              border-radius: 50%;
            }

            .icon-box {
              margin-left: 5px;
            }

            .bg-color1 {
              background: var(--table-cell-status-text-color);
            }

            .bg-color2 {
              background: var(--runing-status-color);
            }

            .bg-color3 {
              background: var(--error-status-color);
            }

            .bg-color4 {
              background: var(--success-status-color);
            }

            .color1 {
              color: var(--table-cell-status-text-color);
            }

            .color2 {
              color: var(--runing-status-color);
            }

            .color3 {
              color: var(--error-status-color);
            }

            .color4 {
              color: var(--success-status-color);
            }
          }
        }
      }

      .common-pagination-box {
        text-align: center;
      }
    }
  }
}
</style>

<style lang="less">
.custom-dropdown-primary-sceneList {
  width: 134px !important;

  .dropdown-item-box {
    width: 100%;

    &:not(.is-disabled):hover {
      background: var(--base-bg-light-color24);
      color: var(--base-primary-bgColor);
      border-radius: 4px;
    }

    .el-dropdown-menu__item {
      width: auto;
      padding-right: 8px;

      &:hover {
        background: none;
      }
    }
  }
}
</style>
